import { useState, useRef, useMemo, useEffect, useCallback } from 'react'
import type React from 'react'
import cn from 'classnames'
import { FaPencilAlt, FaArrowLeft } from 'react-icons/fa'
import sortBy from 'lodash/sortBy'
import map from 'lodash/map'
import debounce from 'lodash/debounce'
import { createId } from 'lib/id'
import Modal from 'components/utils/Modal'
import CloseButton from 'components/utils/CloseButton'
import { physicsSettings } from 'layout/settings'
import type { EdgeDirectionConstraint, KindEntity, KindMap, KindStyles } from 'types/domain'

import './kindSelectModal.less'

type KindSelectModalProps = {
  kinds?: KindMap | undefined
  kindId: string
  title: string
  onSelectKind: (kindId: string) => void
  onClose: () => void
  open: boolean
  objectType?: string | undefined
  editOneKind?: ((payload: { id: string; label: string; styles: Record<string, unknown> }) => void) | undefined
  createOneKind?: ((payload: { id: string; label: string; styles: { color: string } }) => void) | undefined
}

const DIRECTION_OPTIONS: Array<{ value: EdgeDirectionConstraint; label: string }> = [
  { value: 'free', label: 'Free' },
  { value: 'ltr', label: '\u2192 Left to right' },
  { value: 'rtl', label: '\u2190 Right to left' },
  { value: 'ttb', label: '\u2193 Top to bottom' },
  { value: 'btt', label: '\u2191 Bottom to top' },
]

function parseOptionalNumber(raw: string): number | undefined {
  if (raw.trim() === '') {
    return undefined
  }
  const num = Number(raw)
  return Number.isNaN(num) ? undefined : num
}

type KindUIEvent =
  | React.MouseEvent<HTMLElement>
  | React.TouchEvent<HTMLElement>
  | React.KeyboardEvent<HTMLElement>

function normalizeColorValue(color: unknown) {
  if (color == null) {
    return '#888888'
  }
  const str = String(color).trim()
  if (/^#[0-9a-f]{6}$/i.test(str)) {
    return str.toLowerCase()
  }
  if (/^#[0-9a-f]{3}$/i.test(str)) {
    const r = str[1]
    const g = str[2]
    const b = str[3]
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }
  return '#888888'
}

const KindSelectModal = ({
  kinds,
  kindId,
  title,
  onSelectKind,
  onClose,
  open,
  objectType,
  editOneKind,
  createOneKind,
}: KindSelectModalProps) => {
  const [newKindOpen, setNewKindOpen] = useState(false)
  const [newKindLabel, setNewKindLabel] = useState('')
  const [newKindColor, setNewKindColor] = useState('#888888')
  const newKindNameRef = useRef<HTMLInputElement>(null)
  const [editingKindId, setEditingKindId] = useState<string | null>(null)

  // Debounced on the actual write only, so the merge above always sees fresh styles
  const debouncedEditKind = useMemo(
    () =>
      debounce((kId: string, label: string, nextStyles: KindStyles) => {
        editOneKind?.({ id: kId, label, styles: nextStyles })
      }, 200),
    [editOneKind],
  )

  const updateKindStyle = useCallback(
    (kId: string, label: string, patch: Record<string, unknown>) => {
      const existingStyles = kinds?.[kId]?.data?.styles || {}
      const nextStyles = Object.assign({}, existingStyles, patch)
      debouncedEditKind(kId, label, nextStyles)
    },
    [kinds, debouncedEditKind],
  )

  useEffect(() => {
    return () => {
      debouncedEditKind.cancel?.()
    }
  }, [debouncedEditKind])

  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (!open) {
      setNewKindOpen(false)
      setNewKindLabel('')
      setNewKindColor('#888888')
      setEditingKindId(null)
    }
  }

  const close = useCallback((e?: KindUIEvent) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    onClose()
  }, [onClose])

  const selectKind = useCallback((kId: string) => {
    onSelectKind(kId)
  }, [onSelectKind])

  const handleOpenNewKind = useCallback((e?: KindUIEvent) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    setNewKindOpen(true)
    setNewKindLabel('')
    setNewKindColor('#888888')
    setTimeout(() => newKindNameRef.current?.focus?.(), 0)
  }, [])

  const handleCloseNewKind = useCallback((e?: KindUIEvent) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    setNewKindOpen(false)
  }, [])

  const handleCreateNewKind = useCallback((e?: KindUIEvent) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    if (!createOneKind) return
    const label = String(newKindLabel || '').trim()
    if (!label.length) return
    const color = normalizeColorValue(newKindColor)
    const id = createId()
    createOneKind({ id, label, styles: { color } })
    selectKind(id)
  }, [createOneKind, newKindLabel, newKindColor, selectKind])

  const onNewKindKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key
    if (key === 'Enter') {
      handleCreateNewKind(e)
      e.preventDefault()
    } else if (key === 'Escape') {
      handleCloseNewKind(e)
      e.preventDefault()
    }
  }, [handleCreateNewKind, handleCloseNewKind])

  const openEditKind = useCallback((e: KindUIEvent | undefined, kId: string) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    setEditingKindId(kId)
  }, [])

  const closeEditKind = useCallback((e?: KindUIEvent) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    setEditingKindId(null)
  }, [])

  const kindList = sortBy(map(kinds || {}, (k: KindEntity) => k), (k: KindEntity) => (k?.data?.label || '').toLowerCase())
  const editingKind = editingKindId ? kinds?.[editingKindId] : undefined

  return (
    <Modal
      open={open}
      onClose={() => close()}
      className="kind-modal"
    >
      <div className="modal-header">
        <span className="modal-title">{title}</span>
        <CloseButton
          onClick={close}
          onTouchEnd={(e: React.TouchEvent<HTMLButtonElement>) => close(e)}
        />
      </div>
      <div className="modal-body">
        {editingKind ? (
          <div className={cn('kind-detail')} onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <div className={cn('kind-detail-header')}>
              <button
                type="button"
                className={cn('kind-detail-back')}
                onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                onClick={closeEditKind}
                onTouchEnd={(e: React.TouchEvent<HTMLButtonElement>) => closeEditKind(e)}
              >
                <FaArrowLeft aria-hidden /> Back
              </button>
              <span className={cn('kind-detail-label')}>{editingKind.data?.label}</span>
            </div>
            <div className={cn('kind-detail-row')}>
              <span className={cn('kind-detail-row-label')}>Color</span>
              <input
                className={cn('kind-new-color')}
                type="color"
                value={normalizeColorValue(editingKind.data?.styles?.color)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateKindStyle(editingKindId!, editingKind.data?.label || '', { color: e.target.value })
                }
                aria-label="Kind color"
              />
            </div>
            {objectType === 'edge' ? (
              <>
                <div className={cn('kind-detail-divider')} />
                <label className={cn('kind-detail-checkbox-row')}>
                  <input
                    type="checkbox"
                    checked={editingKind.data?.styles?.edgeVisible !== false}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateKindStyle(editingKindId!, editingKind.data?.label || '', { edgeVisible: e.target.checked })
                    }
                  />
                  <span>Visible</span>
                </label>
                <p className={cn('kind-detail-hint')}>When off, this edge only shows while one of its ends is focused.</p>

                <label className={cn('kind-detail-checkbox-row')}>
                  <input
                    type="checkbox"
                    checked={editingKind.data?.styles?.edgeSpring !== false}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateKindStyle(editingKindId!, editingKind.data?.label || '', { edgeSpring: e.target.checked })
                    }
                  />
                  <span>Pulls together</span>
                </label>
                <p className={cn('kind-detail-hint')}>When off, the edge is still drawn but no longer pulls its ends toward each other.</p>

                <div className={cn('kind-detail-field')}>
                  <span className={cn('kind-detail-field-label')}>Stiffness</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    value={editingKind.data?.styles?.edgeStiffness ?? ''}
                    placeholder={String(physicsSettings.stiffness)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateKindStyle(editingKindId!, editingKind.data?.label || '', { edgeStiffness: parseOptionalNumber(e.target.value) })
                    }
                  />
                </div>

                <div className={cn('kind-detail-field')}>
                  <span className={cn('kind-detail-field-label')}>Length</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    value={editingKind.data?.styles?.edgeSpringLength ?? ''}
                    placeholder={String(physicsSettings.springLength)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateKindStyle(editingKindId!, editingKind.data?.label || '', { edgeSpringLength: parseOptionalNumber(e.target.value) })
                    }
                  />
                </div>

                <div className={cn('kind-detail-field')}>
                  <span className={cn('kind-detail-field-label')}>Direction</span>
                  <select
                    value={editingKind.data?.styles?.edgeDirection ?? 'free'}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      updateKindStyle(editingKindId!, editingKind.data?.label || '', {
                        edgeDirection: e.target.value as EdgeDirectionConstraint,
                      })
                    }
                  >
                    {DIRECTION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <label className={cn('kind-detail-checkbox-row', { disabled: (editingKind.data?.styles?.edgeDirection ?? 'free') === 'free' })}>
                  <input
                    type="checkbox"
                    disabled={(editingKind.data?.styles?.edgeDirection ?? 'free') === 'free'}
                    checked={Boolean(editingKind.data?.styles?.edgeDirectionSoft)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateKindStyle(editingKindId!, editingKind.data?.label || '', { edgeDirectionSoft: e.target.checked })
                    }
                  />
                  <span>Soft (only restrict the general direction)</span>
                </label>

                <div className={cn('kind-detail-field')}>
                  <span className={cn('kind-detail-field-label')}>Thickness</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    value={editingKind.data?.styles?.edgeThickness ?? ''}
                    placeholder="1"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateKindStyle(editingKindId!, editingKind.data?.label || '', { edgeThickness: parseOptionalNumber(e.target.value) })
                    }
                  />
                </div>
              </>
            ) : null}
          </div>
        ) : newKindOpen ? (
          <div className={cn('kind-new')} onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
            <div className={cn('kind-new-form')}>
              <input
                ref={newKindNameRef}
                className={cn('kind-new-input')}
                type="text"
                value={newKindLabel}
                placeholder="New kind name"
                maxLength={15}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewKindLabel(e.target.value)}
                onKeyDown={onNewKindKeyDown}
              />
              <input
                className={cn('kind-new-color')}
                type="color"
                value={normalizeColorValue(newKindColor)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewKindColor(e.target.value)}
                aria-label="New kind color"
              />
              <button
                type="button"
                className={cn('kind-new-create')}
                onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                onClick={handleCreateNewKind}
                onTouchEnd={(e: React.TouchEvent<HTMLButtonElement>) => handleCreateNewKind(e)}
              >
                Create
              </button>
            </div>
          </div>
        ) : (
          <div
            className={cn('kind-option', { new: true })}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
            onClick={handleOpenNewKind}
            onTouchEnd={(e: React.TouchEvent<HTMLDivElement>) => handleOpenNewKind(e)}
          >
            <div className={cn('kind-option-left')}>
              <span className={cn('kind-option-label')}>+ New Kind</span>
            </div>
          </div>
        )}
        {editingKind ? null : (
          <div
            className={cn('kind-option', { clear: true, selected: kindId == null || kindId === '' })}
            onClick={() => selectKind('')}
            onTouchEnd={(e: React.TouchEvent<HTMLDivElement>) => {
              e.preventDefault()
              e.stopPropagation()
              selectKind('')
            }}
          >
            <div className={cn('kind-option-left')}>
              <span className={cn('kind-dot')} />
              <span className={cn('kind-option-label')}>No kind</span>
            </div>
          </div>
        )}
        {editingKind ? null : map(kindList, (k: KindEntity) => {
          const label = k?.data?.label
          const color = k?.data?.styles?.color
          if (label == null) {
            return null
          }
          return (
            <div
              key={k.id}
              className={cn('kind-option', { selected: k.id === kindId })}
              onClick={() => selectKind(k.id)}
              onTouchEnd={(e: React.TouchEvent<HTMLDivElement>) => {
                e.preventDefault()
                e.stopPropagation()
                selectKind(k.id)
              }}
            >
              <div className={cn('kind-option-left')}>
                <span className={cn('kind-dot')} style={color != null ? { backgroundColor: color } : undefined} />
                <span className={cn('kind-option-label')} style={color != null ? { color } : undefined}>
                  {label}
                </span>
              </div>
              <div
                className={cn('kind-option-actions')}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                onTouchEnd={(e: React.TouchEvent<HTMLDivElement>) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className={cn('kind-edit-button')}
                  onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => openEditKind(e, k.id)}
                  onTouchEnd={(e: React.TouchEvent<HTMLButtonElement>) => openEditKind(e, k.id)}
                  aria-label="Edit kind"
                >
                  <FaPencilAlt aria-hidden />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </Modal>
  )
}

export default KindSelectModal
